import { v4 as uuid } from 'uuid';
import isArray from 'lodash/isArray';
import Catalyst from '@Services/Catalyst';
import AppStorage from '@Services/AppStorage';

function countWords(text = '') {
  // Remove extra whitespace, split by spaces, filter out empty strings
  return text.trim().split(/\s+/).filter(Boolean).length;
}

let startProcessingTime;

export function startGenAIContentProcessingTimer() {
  startProcessingTime = Date.now();
}

export function stopGenAIContentProcessingTimer() {
  let processingTime;
  if (startProcessingTime) {
    processingTime = Date.now() - startProcessingTime;
  }
  startProcessingTime = undefined;
  return processingTime;
}

export function trackGenAIContentUpdated({ summaryResponse = {}, activeBreadcrumb = {} }) {
  const { summary, referencedCitations } = summaryResponse;
  const { isHistoryBreadcrumb, quickSearchItem } = activeBreadcrumb;

  const processingTime = stopGenAIContentProcessingTimer();

  const quickSearchExampleUsed = AppStorage.instance.getFromSessionStorage('quickSearchExampleUsed');
  const quickSearchContent = quickSearchItem?.content;
  const quickSearchStructure = quickSearchItem?.structure;

  const inputSource = quickSearchExampleUsed && quickSearchExampleUsed === quickSearchItem?.content ? 'example link' : 'user';
  const inputOrigin = isHistoryBreadcrumb ? 'history' : 'first search';
  const citationsCount = referencedCitations?.length;
  const resultWordCount = countWords(summary);
  const conversationId = uuid();
  const promptCounter = '1';

  let answerDetails = 'tools=[]';
  if (quickSearchContent && quickSearchStructure) {
    answerDetails = 'tools=[text + structure search]';
  } else if (quickSearchContent) {
    answerDetails = 'tools=[text search]';
  } else if (quickSearchStructure) {
    answerDetails = 'tools=[structure search]';
  }

  Catalyst.trackEvent('genAIContentUpdated', {
    genAI: {
      inputSource, // GenAI Details [v58], who formulated the input? "user" = user entered his own question, "example link" - user chose one of the questions proposed by the tool
      inputOrigin, // GenAI Details [v58],  where was the input triggered from - first question, follow-up question, etc.
      citationsCount, // GenAI Citations [e342], number of citations returned
      resultWordCount, // GenAI Result Word Count [e340], number of words returned
      answerDetails, // GenAI Answer Details [p45], tools = shows all tools used to generate the answer (e.g. tools to analyse the drawn structures or written text)
      processingTime: processingTime && String(processingTime), // GenAI Processing Time [e362], submitted as string in ms
      conversationId, // GenAI ConversationID and PromptCounter [p46], GenAI Unique Conversations [e364], unique conversation id (unique across all users)
      promptCounter, // GenAI ConversationID and PromptCounter [p46], GenAI Unique Prompts [e365], it can be a simple counter as combined with conversation-id is unique
    },
    content: [], // Anything else you may find relevant to send about the content involved in the AI-generated summary
  });
}

export function trackExpandCollapseSummary(expand) {
  Catalyst.trackEvent('searchFeatureClick', {
    // Search Result Feature Uses [e10]
    search: {
      featureName: expand ? 'rx2:results:citations:ai:view full summary' : 'rx2:results:citations:ai:collapse full summary', // Search Result Feature Pathing [p8]
    },
  });
}

export function trackSummaryGenerationError() {
  Catalyst.trackEvent('formError', {
    form: {
      errorType: 'fe-rx2:results:citations:ai:summary generation failed', // Error Type [v43]
    },
  });
}

export function trackSummaryNotAvailable() {
  Catalyst.trackEvent('formError', {
    form: {
      errorType: 'fe-rx2:results:citations:ai:summary is not shown due to filters', // Error Type [v43]
    },
  });
}

export function trackReferencePreview({ citationFromSummary = {}, citationFromResultsList = {} } = {}) {
  const { fullTextUrl } = citationFromSummary;
  const { index, absoluteIndex } = citationFromResultsList;

  let resultsPosition = index;
  if (absoluteIndex) {
    resultsPosition = isArray(absoluteIndex) ? String(absoluteIndex[0]) : String(absoluteIndex);
  }

  Catalyst.trackEvent('searchResultsClick', {
    content: [
      {
        format: 'MIME-XHTML', // File/Document/Multimedia Format [v17]
        type: 'ai-summary-reference', // Content Type [v20]
      },
    ],
    search: {
      resultsPosition, // Search Click Position [v15], the absolute position in the list of search results starting with "1"
    },
    link: {
      name: fullTextUrl,
    },
  });
}

export function trackSurveySubmissionForSummary(liked) {
  Catalyst.trackEvent('SurveySubmission', {
    survey: {
      question: 'Was this summary helpful?', // Survey Meta Data [p43, v56]
      score: liked ? '2' : '1', // Survey Score [e322] - 1 for Thumb Down, 2 for Thumb Up
      targeting: 'Reaxys AI - Summary Evaluation', // Survey Meta Data [p43, v56]
      type: 'GenAI-Summary-BinaryScore', // Survey Meta Data [p43, v56]
    },
  });
}

export function trackSurveySubmissionForReferencePreview(liked) {
  Catalyst.trackEvent('SurveySubmission', {
    survey: {
      question: 'Was this relevant to the question?', // Survey Meta Data [p43, v56]
      score: liked ? '2' : '1', // Survey Score [e322] - 1 for Thumb Down, 2 for Thumb Up
      targeting: 'Reaxys AI - Reference Preview Evaluation', // Survey Meta Data [p43, v56]
      type: 'GenAI-References-BinaryScore', // Survey Meta Data [p43, v56]
    },
  });
}

export function trackHowItWorksClick() {
  Catalyst.trackEvent('buttonClick', {
    // Button Clicks [e204]
    buttonType: 'rx2:results:citations:type=help:name=ai-how-it-works', // Button Type [v124]
  });
}

export function trackCopyToClipboard() {
  Catalyst.trackEvent('copyToClipboard', {
    content: [
      {
        type: 'reaxys-ai:answer-copy', // Content Type [v20]
      },
    ],
  });
}
